import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const GAP = 12;
const EDGE_PADDING = 8;
const ARROW_SIZE = 7;

/* Info icon + tooltip shown next to subcategories that have a description hint. */
export const SubcategoryInfoIcon = ({ description }) => {
  const [phase, setPhase] = useState('closed'); // closed | measuring | visible
  const [coords, setCoords] = useState({ top: 0, left: 0, arrowLeft: 0, placement: 'top' });
  const iconRef = useRef(null);
  const tooltipRef = useRef(null);
  const timerRef = useRef(null);

  const open = () => setPhase('measuring');
  const close = () => { setPhase('closed'); clearTimeout(timerRef.current); };

  useLayoutEffect(() => {
    if (!description || phase !== 'measuring' || !tooltipRef.current || !iconRef.current) return;
    const iconRect = iconRef.current.getBoundingClientRect();
    const ttRect = tooltipRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let placement = 'top';
    let top = iconRect.top - ttRect.height - GAP;
    if (top < EDGE_PADDING) {
      // Not enough room above -> fall back below the icon
      placement = 'bottom';
      top = iconRect.bottom + GAP;
      if (top + ttRect.height > vh - EDGE_PADDING) {
        // Not enough room below either -> clamp to viewport
        top = Math.max(EDGE_PADDING, vh - ttRect.height - EDGE_PADDING);
      }
    }

    let left = iconRect.left + iconRect.width / 2 - ttRect.width / 2;
    left = Math.max(EDGE_PADDING, Math.min(left, vw - ttRect.width - EDGE_PADDING));

    const iconCenter = iconRect.left + iconRect.width / 2;
    const arrowLeft = Math.max(ARROW_SIZE + 2, Math.min(iconCenter - left, ttRect.width - ARROW_SIZE - 2));

    setCoords({ top, left, arrowLeft, placement });
    setPhase('visible');
  }, [phase]);

  const handleTap = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (phase !== 'closed') { close(); return; }
    open();
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(close, 5000);
  };

  if (!description) return null;

  const shown = phase !== 'closed';
  const visible = phase === 'visible';

  return (
    <>
      <span
        ref={iconRef}
        onMouseEnter={open}
        onMouseLeave={close}
        onPointerDown={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
        onClick={handleTap}
        className="ml-2 inline-flex w-[18px] h-[18px] shrink-0 rounded-full border border-[#2E6F57] text-[#2E6F57] items-center justify-center text-[12px] font-bold leading-none cursor-pointer select-none"
        data-testid="subcategory-info-icon"
        aria-label={description}
      >
        i
      </span>
      {shown && createPortal(
        <div
          ref={tooltipRef}
          className="fixed z-[10000] max-w-[220px] whitespace-normal rounded bg-[#333] text-white text-[12px] leading-[1.4] px-[14px] py-[10px] shadow-[0_4px_12px_rgba(0,0,0,0.3)] pointer-events-none"
          style={{ top: coords.top, left: coords.left, opacity: visible ? 1 : 0 }}
          data-testid="subcategory-info-tooltip"
        >
          {description}
          <span
            className="absolute"
            style={{
              left: coords.arrowLeft - ARROW_SIZE,
              ...(coords.placement === 'top'
                ? { bottom: -ARROW_SIZE, borderLeft: `${ARROW_SIZE}px solid transparent`, borderRight: `${ARROW_SIZE}px solid transparent`, borderTop: `${ARROW_SIZE}px solid #333` }
                : { top: -ARROW_SIZE, borderLeft: `${ARROW_SIZE}px solid transparent`, borderRight: `${ARROW_SIZE}px solid transparent`, borderBottom: `${ARROW_SIZE}px solid #333` }),
            }}
          />
        </div>,
        document.body
      )}
    </>
  );
};
